import { AfterViewInit, Component, computed, inject, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Router, RouterModule } from '@angular/router';
import { formatDate } from '@angular/common';

import { AppStringsPipe } from '../shared/pipes/appstrings.pipe';
import { default as appString } from '../../assets/resources/appstrings.da.json'
import { MatButtonModule } from '@angular/material/button';
import { DataStoreReaderService } from '../shared/services/data-store/viewmodels/data-store-reader-viewmodels.service';
import { TrialOverviewWeighingDateViewModel } from '../shared/viewmodels/trials/trial-overview-weighing-date.viewmodel';
import { TrialOverviewViewModel } from '../shared/viewmodels/trials/trial-overview.viewmodel';
import { ApprovalStatusType } from '../shared/domain/model/approval-status-type';
import { ApprovalStatusTypeEnum } from '../shared/domain/model/model-enums';
import { TrialOverviewWeighingWeightViewModel } from '../shared/viewmodels/trials/trial-overview-weighing-weight.viewmodel';

@Component({
  selector: 'app-trials',
  templateUrl: './trials.component.html',
  imports: [
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatPaginatorModule,
    AppStringsPipe,
  ]
})
export class TrialsComponent implements AfterViewInit {
  readonly DATE_FORMAT = 'dd-MM-yyyy';
  readonly LOCALE = 'en-US';


  readonly COL_TRIAL = 'trial';
  readonly COL_SECTION = 'section';
  readonly COL_BATCH = 'batch';
  readonly COL_PEN = 'pen';
  readonly COL_INITIAL_WEIGHING = 'initialWeighing';
  readonly COL_FIRST_WEIGHING = 'firstWeighing';
  readonly COL_SECOND_WEIGHING = 'secondWeighing';
  readonly COL_FINAL_WEIGHING = 'finalWeighing';

  protected displayedColumns: ReadonlyArray<string> = [this.COL_TRIAL, this.COL_SECTION, this.COL_BATCH, this.COL_PEN, this.COL_INITIAL_WEIGHING, this.COL_FIRST_WEIGHING, this.COL_SECOND_WEIGHING, this.COL_FINAL_WEIGHING];
  protected dataSource = computed(() => {
    const trials = this.viewModelGetter.getTrialOverviewViewModel();
    const table = new MatTableDataSource(trials);
    return table;
  });

  protected readonly viewModelGetter = inject(DataStoreReaderService)
  @ViewChild(MatPaginator)
  paginator!: MatPaginator;
  @ViewChild(MatSort)
  sort!: MatSort;

  private router = inject(Router);

  constructor() {
  }

  ngAfterViewInit() {
    this.setSortingAndPagination(this.dataSource());
  }

  formatDate (time: Date) : string {
    return formatDate(time, this.DATE_FORMAT, this.LOCALE);
  }

  getWeighingAsText (weighing: TrialOverviewWeighingWeightViewModel | TrialOverviewWeighingDateViewModel) : string {
    if (weighing instanceof TrialOverviewWeighingWeightViewModel)
    {
      return `${appString.GENERAL.FROM} ${weighing.weight} kg`;
    }
    if (weighing instanceof TrialOverviewWeighingDateViewModel)
    {
      return weighing.date ? this.formatDate(weighing.date) : "";
    }

    return "";
  }
  setSortingAndPagination(dataSource: MatTableDataSource<TrialOverviewViewModel>): void {
    dataSource.sort = this.sort;
    dataSource.paginator = this.paginator;

    dataSource.sortingDataAccessor = (item: TrialOverviewViewModel, property) => {
      switch (property) {
        case this.COL_INITIAL_WEIGHING:
          return item.initialWeighing.date ? this.formatDate(item.initialWeighing.date) : "";

        case this.COL_FIRST_WEIGHING:
          return this.getWeighingAsText(item.firstWeighing);

        case this.COL_SECOND_WEIGHING:
          return this.getWeighingAsText(item.secondWeighing);

        case this.COL_PEN:
          return item.penNames;

        case this.COL_BATCH:
          return item.batchNames;

        case this.COL_SECTION:
          return item.section.name;

        case this.COL_TRIAL:
          return item.trialName;

        case this.COL_FINAL_WEIGHING:
          return this.getWeighingAsText(item.finalWeighing);
        default:
          throw new Error(`Unknown property: '${property}'`);
      }
    }
  }

  isEndWeighingCompleted(date?: Date): boolean {
    if (date) {
      return true;
    }
    return false;
  }

  isCompleted(status: ApprovalStatusType) : boolean {
    const is_completed = (status.status === ApprovalStatusTypeEnum.Approved);
    //console.info(`isCompleted: ${JSON.stringify(status)}, is_completed: ${is_completed}`);
    return is_completed;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;

    const dataSource = this.dataSource();
    if (dataSource !== null) {
      dataSource.filter = filterValue.trim().toLowerCase();

      if (dataSource?.paginator) {
        dataSource.paginator.firstPage();
      }
    }
  }

  async clickTrial(trialOverviewViewModel: TrialOverviewViewModel) {
    const sectionId = trialOverviewViewModel.section.id.asString();
    const trialId = trialOverviewViewModel.id.asString();

    await this.router.navigate([`/section-overview/${sectionId}`],
      { queryParams: {trialId: trialId}
      });
  }
}
